import { useCallback, useMemo } from 'react'
import type { SortingState } from '@tanstack/react-table'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { selectDriveListSortForMatter } from '@/user_files/folders/store/selectors'
import { UserFoldersActions } from '@/user_files/folders/store/slice'
import type { DriveListSort, DriveSortKey } from '@/user_files/sorting'

export type DriveListSorting = {
  /** Sort to request from the list endpoints. */
  sort: DriveListSort
  /** The same sort in TanStack's shape, for the table's controlled `sorting` state. */
  sorting: SortingState
  /** Applies a header click. */
  onSortingChange: (sorting: SortingState) => void
}

/**
 * Sorting state for the Matter Files table.
 *
 * The backend owns row order — both list endpoints take `sort_by`/`sort_order` — so this holds only
 * the requested sort and the drive refetches when it changes.
 *
 * The sort is read from and written to `userFoldersState`, keyed by matter, rather than held here.
 * It has to outlive folder navigation: `UserFilesFolderRouteHandler` swaps its children for a
 * spinner until the URL folder and Redux agree, so opening or leaving a folder unmounts the whole
 * drive subtree along with any component-local state it held. By design the sort lasts the whole
 * visit and each matter keeps its own, so returning to a matter shows the sort you left it in and
 * a different matter opens on the default.
 */
export function useDriveListSort(): DriveListSorting {
  const dispatch = useAppDispatch()
  // The drive only renders under a settled matter, so this is the matter whose drive is on screen.
  const activeMatterId = useAppSelector((state) => state.mattersState.activeMatterId)
  const activeSort = useAppSelector((state) => selectDriveListSortForMatter(state, { matterId: activeMatterId }))

  // The design always shows an active column, so there is always exactly one entry and the table
  // never sees an empty sorting state.
  const sorting = useMemo<SortingState>(() => [{ id: activeSort.key, desc: activeSort.direction === 'desc' }], [activeSort])

  const onSortingChange = useCallback(
    (next: SortingState) => {
      if (!activeMatterId) return

      const column = next[0]
      if (!column) return
      dispatch(
        UserFoldersActions.setDriveListSort({
          matterId: activeMatterId,
          sort: { key: column.id as DriveSortKey, direction: column.desc ? 'desc' : 'asc' },
        })
      )
    },
    [activeMatterId, dispatch]
  )

  return { sort: activeSort, sorting, onSortingChange }
}
